/**
 * A fake header search box component for use in the header of the page. This
 * component shows elements that look very similar to the real search box, and
 * when interacted with, loads the real typeahead-package search box in place.
 */

import {t} from "@lingui/core/macro";
import {css, type CSSProperties} from "aphrodite";
import * as React from "react";

import withLocation, {
    type WithLocationProps,
    type WithoutLocation,
} from "#data-access/with-location.tsx";
import type {BloodhoundT} from "#typeahead-package/types.ts";

type SearchBox = typeof import("../typeahead-package/search-box.tsx").default;

type Props = {
    SearchBox: SearchBox | null | undefined;
    onEscape: () => void;
    searchFormStyles?: Array<CSSProperties>;
    searchInputStyles?: Array<CSSProperties>;
    spacer: React.ReactNode;
    // BloodhoundPropType in search-box.jsx
    suggestionSource?: BloodhoundT;
} & WithLocationProps;

class HeaderSearchBox extends React.Component<Props> {
    render(): React.ReactNode {
        const {
            location: {pathname},
            onEscape,
            SearchBox,
            searchInputStyles,
            searchFormStyles,
            spacer,
            suggestionSource,
        } = this.props;

        const placeholder = t`Search for courses, skills, and videos`;
        const dataSources = {
            searchSuggestion: suggestionSource,
        } as const;

        return SearchBox ? (
            <SearchBox
                isBibliotron={true}
                inputClassName={
                    searchInputStyles ? css(...searchInputStyles) : undefined
                }
                formClassName={
                    searchFormStyles ? css(...searchFormStyles) : undefined
                }
                placeholder={placeholder}
                dataSources={dataSources}
                initiallyFocused={true}
                extraFormArgs={{referer: pathname}}
                spacer={spacer}
                onEscape={onEscape}
            />
        ) : null;
    }
}

type ExportProps = WithoutLocation<
    JSX.LibraryManagedAttributes<
        typeof HeaderSearchBox,
        React.ComponentProps<typeof HeaderSearchBox>
    >
>;

export default withLocation(
    HeaderSearchBox,
) as React.ComponentType<ExportProps>;
